import {
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Button,
  TextField,
  Pagination,
  Grid,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDButton from "theme/components/MDButton";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function WhatsAppSystemTemplatesPage() {
  const pageTitle = "WhatsApp - System Templates";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();
  const statusDialog = useAwaitDialog();

  const [showLoading, setShowLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 20;

  const activateTemplate = async (templateReference) => {
    setShowLoading(true);

    await axios
      .post("/portal/wacc/templates/activate.json", {
        reference: ref,
        template_reference: templateReference,
      })
      .then(async (result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          await statusDialog.show(
            "The template has been activated successfully.",
            "success",
            "Template Activated"
          );
        } else {
          await statusDialog.show(result.data.data.message, "error", "Activation Failed");
        }
      })
      .catch(async () => {
        setShowLoading(false);

        await statusDialog.show("Unable to complete request.", "error", "Request Failed");
      });
  };

  const replaceVariables = (text) => {
    if (!text) {
      return "";
    }

    return text.replace(
      /(\{\{[^}]+\}\})/g,
      '<span style="color:#2e7d32;font-weight:500;">$1</span>'
    );
  };

  const renderComponentText = (text) => {
    if (!text) {
      return null;
    }

    return (
      <MDTypography
        variant="body2"
        sx={{
          fontSize: "0.95rem",
          lineHeight: 1.5,
          color: "#303030",
        }}
        dangerouslySetInnerHTML={{
          __html: replaceVariables(text).replace(/\n/g, "<br />"),
        }}
      />
    );
  };

  const renderTemplate = (template) => {
    const components = template.components || [];
    const header = components.find((component) => component.type === "HEADER");
    const body = components.find((component) => component.type === "BODY");
    const footer = components.find((component) => component.type === "FOOTER");
    const buttons = components.find((component) => component.type === "BUTTONS");

    return (
      <MDBox
        sx={{
          backgroundColor: "#efe7dd",
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(120,100,80,0.06) 1px, transparent 1px), radial-gradient(circle at 80% 70%, rgba(120,100,80,0.05) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MDBox
          sx={{
            width: "100%",
            backgroundColor: "#fff",
            borderRadius: "9px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
            overflow: "hidden",
          }}
        >
          <MDBox px={2} py={1.75}>
            {header && header.format === "TEXT" && (
              <MDTypography
                variant="h5"
                fontWeight="bold"
                sx={{
                  fontSize: "1.0rem",
                  lineHeight: 1.25,
                  mb: 1,
                }}
              >
                {header.text}
              </MDTypography>
            )}

            {header && header.format !== "TEXT" && (
              <MDBox
                sx={{
                  height: 70,
                  mb: 1.5,
                  borderRadius: "7px",
                  backgroundColor: "#f1f1f1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  sx={{
                    fontSize: 30,
                    color: "text.secondary",
                  }}
                >
                  image
                </Icon>
              </MDBox>
            )}

            {body && renderComponentText(body.text)}

            {footer && (
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 2,
                  fontSize: "0.7rem",
                }}
              >
                {footer.text}
              </MDTypography>
            )}
          </MDBox>

          {buttons && buttons.buttons && buttons.buttons.length > 0 && (
            <MDBox
              sx={{
                borderTop: "1px solid #e0e0e0",
              }}
            >
              {buttons.buttons.map((button, index) => (
                <MDBox
                  key={index}
                  sx={{
                    py: 0.8,
                    textAlign: "center",
                    borderBottom: index < buttons.buttons.length - 1 ? "1px solid #e0e0e0" : "none",
                  }}
                >
                  <MDTypography
                    variant="button"
                    sx={{
                      color: "#168c7b",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                    }}
                  >
                    {button.text}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          )}

          <MDBox px={1.5} pb={0.75} display="flex" justifyContent="flex-end">
            <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
              7:29 PM
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    );
  };

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) {
      return templates;
    }

    const value = search.toLowerCase();

    return templates.filter((template) => {
      return (
        (template.title || "").toLowerCase().includes(value) ||
        (template.description || "").toLowerCase().includes(value) ||
        (template.category || "").toLowerCase().includes(value) ||
        (template.language || "").toLowerCase().includes(value)
      );
    });
  }, [templates, search]);

  const totalPages = Math.ceil(filteredTemplates.length / rowsPerPage);

  const displayedTemplates = useMemo(() => {
    const start = (page - 1) * rowsPerPage;

    return filteredTemplates.slice(start, start + rowsPerPage);
  }, [filteredTemplates, page]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);

      await axios
        .get("/portal/wacc/templates/system.json")
        .then(async (result) => {
          setShowLoading(false);

          if (result.data.status === "success") {
            setTemplates(result.data.data.templates || []);
          } else {
            await statusDialog.show(result.data.data.message, "error", "Request Failed");
          }
        })
        .catch(async () => {
          setShowLoading(false);

          await statusDialog.show("Unable to complete request.", "error", "Request Failed");
        });
    };

    fetchData();
  }, [ref]);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <MDBox mt={1} mb={5}>
        <MDBox mb={1}>
          <Button
            color="dark"
            size="small"
            startIcon={<Icon>west</Icon>}
            onClick={() => {
              navigate(`/wacc/businesses/${ref}/templates`);
            }}
          >
            Back
          </Button>
        </MDBox>

        <Card>
          <MDBox p={1.5}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium">
                  System Templates
                </MDTypography>

                <MDTypography mt={0.25} variant="button" fontWeight="light" color="text">
                  Pre-defined WhatsApp templates available for activation.
                </MDTypography>
              </MDBox>

              <TextField
                size="small"
                placeholder="Search templates..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                sx={{
                  width: 260,
                }}
              />
            </MDBox>
          </MDBox>
        </Card>

        <MDBox mt={3}>
          <MDTypography
            variant="button"
            color="text"
            align="right"
            sx={{
              display: "block",
              mb: 1,
              ml: 0.25,
              fontSize: "0.8rem",
            }}
          >
            Showing {displayedTemplates.length} of {filteredTemplates.length} results
          </MDTypography>

          <Grid mt={1} container spacing={1.5} alignItems="flex-start">
            {displayedTemplates.map((template) => (
              <Grid item xs={12} sm={6} lg={4} key={template.reference}>
                <Card
                  sx={{
                    overflow: "hidden",
                    borderRadius: "8px",
                    height: "fit-content",
                  }}
                >
                  {renderTemplate(template)}

                  <MDBox p={1}>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.85rem",
                      }}
                    >
                      {template.title}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    px={1.5}
                    py={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <MDBox sx={{ minWidth: 0 }}>
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {template.category} · {template.language}
                      </MDTypography>
                    </MDBox>

                    <MDButton
                      size="small"
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        activateTemplate(template.reference);
                      }}
                    >
                      Activate
                    </MDButton>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>

          {displayedTemplates.length === 0 && (
            <Card>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="button" color="text">
                  No templates found.
                </MDTypography>
              </MDBox>
            </Card>
          )}

          {totalPages > 1 && (
            <MDBox mt={1.5} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => {
                  setPage(value);
                }}
                color="primary"
                size="small"
              />
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default WhatsAppSystemTemplatesPage;
